import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X, UserPlus, Check, Search } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { countryFlag } from "../lib/derive";
import { countryName } from "../lib/countries";
import { TierChip } from "../pages/Leaderboards";
import Avatar from "./Avatar";
import { UserName } from "./UserName";

function expiresMs(iso) {
  if (!iso) return 0;
  return Math.max(0, new Date(iso).getTime() - Date.now());
}
function fmtMs(ms) {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function LobbyInviteFriendsModal({ open, onClose, lobbyCode, currentMembers }) {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [invited, setInvited] = useState({}); // user_id -> expires_at ISO
  const [tick, setTick] = useState(0);
  const memberIds = useMemo(() => new Set((currentMembers || []).map((p) => p.user_id)), [currentMembers]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/friends?status=accepted&limit=200");
        if (!cancelled) setFriends(data.items || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const filtered = friends.filter((row) => {
    const u = row.user.username || "";
    return !q || u.toLowerCase().includes(q.toLowerCase());
  });

  const sendInvite = async (uid) => {
    try {
      const { data } = await api.post(`/lobbies/${lobbyCode}/invite-friend`, { to_user_id: uid });
      setInvited((m) => ({ ...m, [uid]: data.expires_at }));
      toast.success("Invite sent");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to invite";
      toast.error(typeof msg === "string" ? msg : "Failed to invite");
    }
  };

  return (
    <div
      data-testid="invite-friends-modal"
      className="fixed inset-0 z-50 grid place-items-center sm:p-4 p-0 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md sm:rounded-2xl rounded-none h-full sm:h-auto sm:max-h-[80vh] flex flex-col bg-zinc-950 border border-white/10 shadow-2xl"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <h2 className="text-xs uppercase tracking-[0.4em] inline-flex items-center gap-2">
            <UserPlus size={14}/> Invite friends
          </h2>
          <button onClick={onClose}
                  data-testid="invite-friends-modal-close"
                  className="size-8 rounded-md hover:bg-white/5 grid place-items-center">
            <X size={14}/>
          </button>
        </div>

        <div className="p-4 border-b border-white/5">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter friends…"
              className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[var(--accent)] outline-none text-sm"
            />
          </div>
        </div>

        <div className="flex-1 overflow-auto px-2 py-2 space-y-1.5">
          {loading && <div className="text-center py-12 text-xs text-zinc-500">Loading…</div>}
          {!loading && filtered.length === 0 && friends.length === 0 && (
            <div className="text-center py-10 px-6">
              <div className="text-sm text-zinc-400 mb-2">No accepted friends yet</div>
              <Link to="/friends" className="text-[var(--accent)] text-xs uppercase tracking-widest">Go to Friends →</Link>
            </div>
          )}
          {!loading && filtered.length === 0 && friends.length > 0 && (
            <div className="text-center py-8 text-xs text-zinc-500">No match for "{q}"</div>
          )}

          {filtered.map((row) => {
            const u = row.user;
            const alreadyMember = memberIds.has(u.user_id);
            const invitedAt = invited[u.user_id];
            const remMs = invitedAt ? expiresMs(invitedAt) : 0;
            const isInvited = !!invitedAt && remMs > 0;

            return (
              <div key={u.user_id}
                   data-testid={`invite-friend-row-${u.user_id}`}
                   className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md hover:bg-white/[0.03]">
                <Link to={`/users/${u.user_id}`} className="flex items-center gap-3 min-w-0">
                  <div className="relative shrink-0">
                    <Avatar user={u} size="md" />
                    {u.is_online && <span className="absolute -bottom-0.5 -left-0.5 size-2.5 rounded-full bg-emerald-500 ring-2 ring-zinc-950" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold truncate flex items-center gap-2">
                      <UserName user={u} /><span className="text-[10px]">{countryFlag(u.country)}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2">
                      <span className={`text-[10px] uppercase tracking-widest ${u.is_online ? "text-emerald-400" : "text-zinc-600"}`}>
                        {u.is_online ? "online" : "offline · will see invite later"}
                      </span>
                    </div>
                  </div>
                </Link>

                {alreadyMember ? (
                  <span className="text-[11px] uppercase tracking-widest text-zinc-500">In lobby</span>
                ) : isInvited ? (
                  <span data-testid={`invite-pending-${u.user_id}`}
                        className="inline-flex items-center gap-1.5 text-[11px] text-[var(--accent)] uppercase tracking-widest font-mono"
                        data-key={tick}>
                    <Check size={12}/> Invited <span>{fmtMs(remMs)}</span>
                  </span>
                ) : (
                  <button
                    data-testid={`invite-btn-${u.user_id}`}
                    onClick={() => sendInvite(u.user_id)}
                    className="px-3 py-1.5 rounded-lg bg-[var(--accent)]/15 border border-[var(--accent)]/30 text-[11px] text-[var(--accent)] uppercase tracking-widest hover:bg-[var(--accent)]/25"
                  >
                    Invite
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
